import { useEffect, useRef, useState } from 'react';

import toast from 'react-hot-toast';

import { gql } from '@apollo/client';

import { FileState } from '__generated__/globalTypes';
import FileReportTable, { FileReportTableFragments } from '../FileReportTable';
import PoolFromTapeButton, { PoolFromTapeButtonFragments } from '../PoolFromTapeButton';
import PoolFromTapeModal from '../PoolFromTapeModal';
import { TapestryOutcomePanelFile } from './__generated__/TapestryOutcomePanel';
import { RoleGatedButton } from './RoleGatedButton';
import { TapestryCompletionOutcome } from './stages';
import { extractTapestryErrorCode, useTapestryJobActions } from './useTapestryJobActions';
import { tapestryErrorMessage } from './errorMessages';

export const TapestryOutcomePanelFragments = {
  loanTape: gql`
    fragment TapestryOutcomePanelFile on File {
      state
      processing_report {
        ...FileReportTableProcessingReport
      }
      ...PoolFromTapeButtonFile
    }
    ${FileReportTableFragments.processingReport}
    ${PoolFromTapeButtonFragments.loanTape}
  `,
};

export type OutcomeVariant = 'success' | 'info' | 'warning' | 'error' | 'neutral';

export interface OutcomePanelView {
  variant: OutcomeVariant;
  title: string;
  /** Offer a Retry action (pipeline_failed only). */
  canRetry: boolean;
  /** Show the processing-report download links (error outcomes). */
  showReportDownloads: boolean;
  /** Offer Pool-from-tape (successful persist only). */
  showPool: boolean;
}

const PASSWORD_PROTECTED_VIEW: OutcomePanelView = {
  variant: 'error',
  title: 'Password protected',
  canRetry: false,
  showReportDownloads: false,
  showPool: false,
};

/**
 * Pure routing from `completion_outcome` to the terminal panel's shape. Returns
 * null for in_progress / unknown (no panel). This is the single place terminal
 * outcomes are interpreted — keyed off completion_outcome, never current_stage.
 * A finished run on a password-protected file gets its own panel: retrying
 * can't read an encrypted file.
 */
export function outcomePanelView(
  outcome: TapestryCompletionOutcome | string | undefined,
  fileState?: FileState,
): OutcomePanelView | null {
  const view = outcomeView(outcome);
  return view && fileState === FileState.ERROR_PASSWORD ? PASSWORD_PROTECTED_VIEW : view;
}

function outcomeView(
  outcome: TapestryCompletionOutcome | string | undefined,
): OutcomePanelView | null {
  switch (outcome) {
    case 'success':
      return {
        variant: 'success',
        title: 'Loans saved to your portfolio',
        canRetry: false,
        showReportDownloads: false,
        showPool: true,
      };
    case 'success_no_persist':
      return {
        variant: 'info',
        title: 'No new loans to save',
        canRetry: false,
        showReportDownloads: false,
        showPool: false,
      };
    case 'no_records':
      return {
        variant: 'warning',
        title: 'No loans found in this tape',
        canRetry: false,
        showReportDownloads: false,
        showPool: false,
      };
    case 'validation_error':
      return {
        variant: 'error',
        title: 'We couldn’t validate this tape',
        canRetry: false,
        showReportDownloads: true,
        showPool: false,
      };
    case 'pipeline_failed':
      return {
        variant: 'error',
        title: 'Processing failed',
        canRetry: true,
        showReportDownloads: true,
        showPool: false,
      };
    case 'cancelled':
      return {
        variant: 'neutral',
        title: 'Processing cancelled',
        canRetry: false,
        showReportDownloads: false,
        showPool: false,
      };
    case 'rejected':
      return {
        variant: 'neutral',
        title: 'Tape rejected',
        canRetry: false,
        showReportDownloads: false,
        showPool: false,
      };
    default:
      // in_progress or any unrecognized value → no terminal panel
      return null;
  }
}

const VARIANT_CLASS: Record<OutcomeVariant, string> = {
  success: 'border-green-500 bg-green-50',
  info: 'border-blue-400 bg-blue-50',
  warning: 'border-yellow-500 bg-yellow-50',
  error: 'border-red-500 bg-red-50',
  neutral: 'border-slate-300 bg-slate-50',
};

interface Props {
  outcome: TapestryCompletionOutcome | string | undefined;
  /** Server-generated banner text; rendered verbatim when present. */
  completionMessage?: string | null;
  selectedFile: TapestryOutcomePanelFile;
  /**
   * Called after a retry kicks off so the caller can resume polling. May return
   * the refetch promise so we can re-enable Retry if that refresh fails.
   */
  onRetried: () => void | Promise<unknown>;
}

/**
 * Terminal/recovery panel for a finished Tapestry run, routed by
 * `completion_outcome`. Shows the server's completion_message, report downloads
 * on errors, Retry on pipeline failures, and Pool-from-tape on success.
 */
export const TapestryOutcomePanel = ({
  outcome,
  completionMessage,
  selectedFile,
  onRetried,
}: Props): JSX.Element | null => {
  const view = outcomePanelView(outcome, selectedFile.state);
  const { retry, loading } = useTapestryJobActions();
  const [poolModalOpen, setPoolModalOpen] = useState(false);
  // Latch retry so it can't fire twice in the window between the mutation
  // resolving and the refetch updating completion_outcome (which unmounts this
  // panel). Self-heals if the refetch fails so the user isn't stuck.
  const [retried, setRetried] = useState(false);
  // Synchronous re-entry guard: `retried`/`loading` only disable the button on
  // the next render, so a double-click could fire two retryTapestryJob calls in
  // the same tick. The ref blocks the second immediately.
  const retrying = useRef(false);
  // Clear both latches whenever the outcome transitions — so a retry that lands
  // on a fresh terminal state (or any change) re-enables the action rather than
  // staying disabled until a remount.
  useEffect(() => {
    setRetried(false);
    retrying.current = false;
  }, [outcome]);

  if (!view) {
    return null;
  }

  const onRetry = async () => {
    if (retrying.current || retried) return;
    retrying.current = true;
    try {
      await retry(selectedFile.id);
    } catch (error) {
      retrying.current = false;
      toast.error(tapestryErrorMessage(extractTapestryErrorCode(error)));
      return;
    }
    setRetried(true);
    try {
      await onRetried();
    } catch {
      setRetried(false);
      retrying.current = false;
    }
  };

  return (
    <>
      <div
        className={`rounded border p-5 text-sm ${VARIANT_CLASS[view.variant]}`}
        data-testid="tapestry-outcome-panel"
        data-outcome={outcome}
        data-variant={view.variant}
      >
        <h2 className="mb-1 text-lg text-gray-700">{view.title}</h2>
        {completionMessage && <p className="my-1 text-sm text-gray-600">{completionMessage}</p>}

        {view.showReportDownloads && selectedFile.processing_report && (
          <div className="mt-3">
            <FileReportTable processingReport={selectedFile.processing_report} />
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
          {view.canRetry && (
            <RoleGatedButton
              label="Retry processing"
              data-testid="tapestry-retry-button"
              disabled={loading || retried}
              onClick={() => void onRetry()}
            >
              Retry processing
            </RoleGatedButton>
          )}
          {view.showPool && (
            <PoolFromTapeButton
              selectedFile={selectedFile}
              onPoolFromTape={() => setPoolModalOpen(true)}
              // showPool is true only for the `success` outcome, which means the
              // pipeline persisted loans — surface the CTA off that signal
              // rather than the lagging File.state (which may never reach
              // PERSISTED for a Tapestry run).
              persistedOverride={outcome === 'success'}
            />
          )}
        </div>
      </div>

      {view.showPool && (
        <PoolFromTapeModal
          open={poolModalOpen}
          onClose={() => setPoolModalOpen(false)}
          tape={{
            tapeId: selectedFile.id,
            tapeName: selectedFile.name ?? selectedFile.file_name,
            assetClass: selectedFile.asset_class,
          }}
        />
      )}
    </>
  );
};
